/**
 * useOnboardingAiBuddy — picks the member's AI companion during onboarding and
 * assigns it, so the "Meet your AI Buddy" step shows a real buddy instead of a
 * placeholder.
 *
 * UserRelation "Agent" is a toggle, not a setter: sending it for a buddy the
 * member already follows would un-assign them. So the assignment is skipped
 * when the roster already reports `isFollowing`, and a successful call is never
 * repeated.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import aiBuddyService from '../api/services/aiBuddyService';
import profileService from '../api/services/profileService';
import { resolveAgentUserId } from '../utils/aiBuddyAgent';
import { getApiErrorMessage } from '../utils/apiErrorHandler';
import type { ApiError } from '../types/api';
import type { AIBuddy } from '../types/user';

export interface UseOnboardingAiBuddyResult {
  /** The recommended buddy, once the roster has loaded. Null when it is empty. */
  buddy: AIBuddy | null;
  loading: boolean;
  /** True once the buddy is confirmed as the member's AI companion. */
  assigned: boolean;
  /** True when the roster loaded but the buddy could not be assigned. */
  assignFailed: boolean;
  /** API failure detail, already user-readable. Empty when there is none. */
  error: string;
  retry: () => void;
}

export const useOnboardingAiBuddy = (): UseOnboardingAiBuddyResult => {
  const [buddy, setBuddy] = useState<AIBuddy | null>(null);
  const [loading, setLoading] = useState(true);
  const [assigned, setAssigned] = useState(false);
  const [assignFailed, setAssignFailed] = useState(false);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  /** Set only after the UserRelation call resolves, so a cancelled mid-flight
   *  attempt never leaves a false "already assigned" guard behind. Survives a
   *  retry so a confirmed assignment is never toggled back off. */
  const assignedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const run = async (): Promise<void> => {
      setLoading(true);
      setError('');
      setAssignFailed(false);

      try {
        const { data } = await aiBuddyService.getAIBuddies();
        const roster = data ?? [];
        // Keep the buddy the member already follows; otherwise the roster leads
        // with the recommendation.
        const recommended = roster.find(candidate => candidate.isFollowing) ?? roster[0] ?? null;

        if (cancelled) return;
        setBuddy(recommended);
        if (!recommended) return;

        if (recommended.isFollowing || assignedRef.current) {
          setAssigned(true);
          return;
        }

        const agentUserId = await resolveAgentUserId(recommended);
        if (cancelled) return;

        if (!agentUserId) {
          setAssignFailed(true);
          return;
        }

        await profileService.userRelation({ relatedUserId: agentUserId, action: 'Agent' });
        // The call succeeded — mark the ref even if the effect was cancelled,
        // so a retry doesn't toggle the buddy back off.
        assignedRef.current = true;
        if (cancelled) return;
        setAssigned(true);
      } catch (caught) {
        // Clear before the cancelled check so a failed or cancelled attempt
        // never leaves a stale "already assigned" guard for the next run.
        assignedRef.current = false;
        if (cancelled) return;
        setAssignFailed(true);
        setError(getApiErrorMessage(caught as ApiError));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run().catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const retry = useCallback(() => setAttempt(count => count + 1), []);

  return { buddy, loading, assigned, assignFailed, error, retry };
};

export default useOnboardingAiBuddy;
